/**
 * Share-target URL builders.
 *
 * Every target is a plain `https://` or `mailto:`/`sms:` URL opened in a new
 * tab — no SDKs, no tracking pixels, nothing to keep up to date. WhatsApp's
 * `wa.me` endpoint is already used elsewhere in the app for invoice sharing;
 * this generalises that rather than adding a second convention.
 */

export type ShareChannel =
  | 'whatsapp'
  | 'email'
  | 'sms'
  | 'telegram'
  | 'copy'
  | 'native';

export interface ShareContent {
  /** Short title — used as the email subject and the native-share title. */
  title: string;
  /** Message body. The URL is appended by builders that need it inline. */
  message: string;
  /** The link being shared. */
  url: string;
}

/**
 * Compose the message body a text-only channel sends.
 *
 * The URL goes on its own line: WhatsApp and SMS clients only auto-link a URL
 * that isn't glued to surrounding punctuation, and a link that doesn't turn
 * blue mostly doesn't get tapped.
 */
export function composeShareText({ message, url }: ShareContent): string {
  const body = message.trim();
  // Some documents have no shareable link — a purchase order, for instance,
  // goes to a supplier who has no account and no public page. Appending an
  // empty URL would leave the message trailing two blank lines.
  return url.trim() ? `${body}\n\n${url.trim()}` : body;
}

export function shareHref(channel: ShareChannel, content: ShareContent): string {
  const text = composeShareText(content);
  switch (channel) {
    case 'whatsapp':
      // No phone number: opens the contact picker so the sender chooses.
      return `https://wa.me/?text=${encodeURIComponent(text)}`;
    case 'telegram':
      return `https://t.me/share/url?url=${encodeURIComponent(content.url)}&text=${encodeURIComponent(content.message.trim())}`;
    case 'email':
      return `mailto:?subject=${encodeURIComponent(content.title)}&body=${encodeURIComponent(text)}`;
    case 'sms':
      // `?&body=` is the form both iOS and Android accept; iOS alone wants `&`.
      return `sms:?&body=${encodeURIComponent(text)}`;
    default:
      return content.url;
  }
}

/**
 * Send to a channel.
 *
 * Returns what happened so callers can show the right feedback — a silent
 * clipboard write with no toast reads as a broken button.
 */
export async function share(
  channel: ShareChannel,
  content: ShareContent,
): Promise<'opened' | 'copied' | 'dismissed' | 'unsupported'> {
  if (channel === 'copy') {
    await navigator.clipboard.writeText(content.url);
    return 'copied';
  }

  if (channel === 'native') {
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (!nav.share) return 'unsupported';
    try {
      await nav.share({ title: content.title, text: content.message.trim(), url: content.url });
      return 'opened';
    } catch {
      // The share sheet rejects on user dismissal — not an error worth surfacing.
      return 'dismissed';
    }
  }

  window.open(shareHref(channel, content), '_blank', 'noopener,noreferrer');
  return 'opened';
}

/** True when the browser can offer the OS share sheet. */
export function canShareNatively(): boolean {
  return typeof navigator !== 'undefined' && typeof (navigator as Navigator & { share?: unknown }).share === 'function';
}
